'use strict';

const HTMLCustomElement = require('@emartech/ui-framework-utils').HTMLCustomElement;
const borderTemplate = require('./border.pug');
const colors = require('@emartech/ui-framework-variables').palettes;

class EVceBorderer extends HTMLCustomElement {
  init() {
    this._observer = new MutationObserver(() => {
      this._borders.forEach(border => {
        this._render(border);
      });
    });

    this._borders = [];
    this._inmost = false;
    this._highlight = false;
    this._target = null;
    this._borderStyle = 'dashed';
    this._borderColor = 'rgba(68,68,68,.8)';
    this._sticky = true;

    this._events = {};
    this._events.over = this._over.bind(this);
    this._events.out = this._out.bind(this);
    this._events.click = this._click.bind(this);
    this._events.clickOutside = this._clickOutside.bind(this);
  }

  connectedCallback() {
    window.addEventListener('unload', () => {
      this._hideAll();
      this._unBindEvents();
    });
    this._hideAll();
    this._collectBorders();
    this._toggleAll();
  }

  disconnectedCallback() {
    this._hideAll();
    this._unBindEvents();
  }

  set highlight(value) {
    this._highlight = this._convertAttributeToBoolean(value);
    this._toggleAll();
  }

  set target(value) {
    this._target = value;
    this._collectBorders();
    this._toggleAll();
  }

  get target() {
    return this._target;
  }

  get _targetElements() {
    return typeof this._target === 'string' ? document.querySelectorAll(this._target) : [this._target];
  }

  set inmost(value) {
    this._inmost = this._convertAttributeToBoolean(value);
    this._collectBorders();
    this._toggleAll();
  }

  set borderstyle(value) {
    this._borderStyle = value;
  }

  set sticky(value) {
    this._sticky = this._convertAttributeToBoolean(value);
    this._bindEvents();
  }

  static get observedAttributes() {
    return ['target', 'highlight', 'borderstyle', 'inmost', 'sticky'];
  }

  filterElementToBorder(element) {
    return this._borders.filter(border => border.element === element)[0];
  }

  _collectBorders() {
    if (!this._target) {
      return;
    }

    if (this._borders) {
      [].forEach.call(this._borders, border => {
        if (border.borderElement.parentNode) {
          border.borderElement.parentNode.removeChild(border.borderElement);
        }
      });
    }

    this._borders = [].map.call(this._targetElements, element => (
      { element, borderElement: document.createElement('e-vce-borderer-element') }
    )).filter(this._inmostFilter.bind(this));
  }

  _inmostFilter(border) {
    if (!this._inmost) {
      return true;
    }

    return !border.element.querySelector(this._target);
  }

  _over(event) {
    const elementToBorder = this.filterElementToBorder(event.currentTarget);
    if (!elementToBorder || elementToBorder.active) {
      return;
    }

    this._render(elementToBorder);
    this._show(elementToBorder.borderElement);
    elementToBorder.element.addEventListener('mouseleave', this._events.out);
  }

  _out(event) {
    const elementToBorder = this.filterElementToBorder(event.currentTarget);
    if (!elementToBorder) {
      return;
    }

    this._hide(elementToBorder.borderElement);
    elementToBorder.element.removeEventListener('mouseleave', this._events.out);
  }

  _click(event) {
    event.stopPropagation();
    event.preventDefault();
    const elementToBorder = this.filterElementToBorder(event.currentTarget);
    if (!elementToBorder) {
      return;
    }

    this._hideAll();
    this._render(elementToBorder);

    const index = this._borders.indexOf(elementToBorder);
    this._borders[index].active = true;

    document.body.addEventListener('click', this._events.clickOutside);
    if (top) {
      top.document.body.addEventListener('click', this._events.clickOutside);
    }
  }

  _show(borderElement) {
    document.body.appendChild(borderElement);
  }

  _hide(borderElement) {
    if (!borderElement.parentNode) {
      return;
    }

    borderElement.parentNode.removeChild(borderElement);
  }

  _toggleAll() {
    const action = this._highlight ? '_showAll' : '_hideAll';
    this[action]();
  }

  _showAll() {
    this._borders.forEach(border => {
      this._render(border);
      this._observer.observe(border.element, { childList: true, characterData: true, subtree: true });
      this._show(border.borderElement);
    });
    this._unBindEvents();
  }

  _hideAll() {
    this._borders.forEach(border => {
      this._hide(border.borderElement);
      this._observer.disconnect();
      border.active = false;
    });
    this._bindEvents();
  }

  _bindEvents() {
    this._unBindEvents();
    this._borders.forEach(border => {
      border.element.addEventListener('mouseover', this._events.over);
      if (!this._sticky) {
        border.element.addEventListener('click', this._events.click);
      }
    });
  }

  _unBindEvents() {
    this._borders.forEach(border => {
      border.element.removeEventListener('mouseover', this._events.over);
      border.element.removeEventListener('click', this._events.click);
    });

    document.body.removeEventListener('click', this._events.clickOutside);
    if (window.top) {
      window.top.document.body.removeEventListener('click', this._events.clickOutside);
    }
  }

  _render(elementToBorder) {
    const elementDimensions = this._getTargetDimensions(elementToBorder.element);
    const containerOffset = {
      scrollLeft: document.defaultView.pageXOffset || document.documentElement.scrollLeft,
      scrollTop: document.defaultView.pageYOffset || document.documentElement.scrollTop
    };
    const warning = this._convertAttributeToBoolean(elementToBorder.element.getAttribute('e-warning'));


    try {
      const url = (location.href !== 'about:blank' ? location.href : window.top.location.href)
        .replace(/session_id=[^&]+/, 'session_id=***');
      const isTest = url.includes('wtr-session-id');
      if (!isTest && warning && !window.elementWithWarningLogged) {
        this._sendLog({
          message: 'EVceBorderer: Element with warning attribute found',
          data: {
            url,
            element: elementToBorder.element.outerHTML
          }
        });
        window.elementWithWarningLogged = true;
      }

      if (
        !isTest &&
        !window.missingCssVariableLogged &&
        getComputedStyle(document.documentElement).getPropertyValue('--token-box-default-border') === ''
      ) {
        this._sendLog({
          message: 'EVceBorderer: --token-box-default-border CSS variable is not set',
          data: {
            url,
            element: elementToBorder.element.outerHTML
          }
        });
        window.missingCssVariableLogged = true;
      }
    } catch (e) {
      this._sendLog({
        message: 'Unexpected exception thrown in EVceBorderer dead code logger',
        data: {
          error_message: e.message
        }
      });
    }

    this._css(elementToBorder.borderElement, {
      left: elementDimensions.left + containerOffset.scrollLeft + 'px',
      top: elementDimensions.top + containerOffset.scrollTop + 'px',
      willChange: 'top, left',
      position: 'absolute',
      display: 'block',
      zIndex: 600
    });

    elementToBorder.borderElement.innerHTML = borderTemplate({
      borderStyle: this._borderStyle,
      borderColor: warning ? colors.warning.default : this._borderColor,
      borderWidth: 3,
      elementWidth: elementDimensions.width,
      elementHeight: elementDimensions.height
    });
  }

  _sendLog(body) {
    const logBaseUrl = 'https://html-email-editor.gservice.emarsys.net/createlog';
    fetch(logBaseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    }).catch(() => { });
  }

  _clickOutside(event) {
    const activeElement = this._borders.filter(border => border.active)[0];
    if (!activeElement || event.target === activeElement.element) {
      return;
    }

    this._hideAll();

    document.body.removeEventListener('click', this._events.clickOutside);
    if (window.top) {
      window.top.document.body.removeEventListener('click', this._events.clickOutside);
    }
  }

  _css(element, styles) {
    for (let property in styles) {
      element.style[property] = styles[property];
    }
  }

  _getTargetDimensions(element) {
    let elementDimensions = null;
    const targetIsInline = window.getComputedStyle(element).getPropertyValue('display') === 'inline';

    if (targetIsInline) {
      const originalStyle = element.getAttribute('style');
      this._setTargetToInlineBlock(element);
      elementDimensions = element.getBoundingClientRect();
      this._resetTargetStyle(element, originalStyle);
    } else {
      elementDimensions = element.getBoundingClientRect();
    }

    return elementDimensions;
  }

  _setTargetToInlineBlock(element) {
    element.style.display = 'inline-block';
  }

  _resetTargetStyle(element, originalStyle) {
    if (originalStyle) {
      element.setAttribute('style', originalStyle);
    } else {
      element.getAttribute('style'); // chrome render fix :(
      element.removeAttribute('style');
    }
  }
}

module.exports = EVceBorderer;
